import { isURLFieldValueValid } from '@modules/card-helpers';
import { isEmail, isUrl } from '@modules/validators';
import { useEffect, useRef, useState } from 'react';

interface Validator {
  message: string;
  priority: number;
  validate: (value: string) => boolean;
  /* If false, the validator will return a warning instead of an error */
  preventSubmit?: boolean;
}

interface UseValidateInputOptions {
  validate?: boolean;
}

/**
 * Given a particular value apply 1 or more validator functions and return whether they all pass and if some fail,
 * the error corresponding to the highest priority validation
 * @param value The value to validate
 * @param validators A set of validation objects containing the validation functions and metadata (message + priority)
 */
export default function useValidateInput(
  value: string,
  validators: Validator[] = [],
  options?: UseValidateInputOptions,
): [boolean, string, boolean] {
  const [isError, setIsError] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const optionsRef = useRef(options);

  // We don't want the options object to cause the observer to disconnect on every render
  // and forcing the user to memoize the object is stupid so storing it on a ref is the next
  // best option since effects run in order.
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    if (optionsRef.current?.validate === false) return;

    const failedValidator = getHighestPriorityFailedValidator(validators, value);

    if (failedValidator) {
      // Reset the error and warning state
      setIsError(false);
      setIsWarning(false);

      // default preventSubmit to true if it is undefined
      const preventSubmit = failedValidator.preventSubmit ?? true;
      preventSubmit ? setIsError(true) : setIsWarning(true);

      setErrorMessage(failedValidator.message);
    } else {
      setIsError(false);
      setIsWarning(false);
      setErrorMessage('');
    }
  }, [value, validators]);

  return [isError, errorMessage, isWarning];
}

function getHighestPriorityFailedValidator(validators: Validator[], value: string): Validator | undefined {
  if (!validators) return;
  if (Array.isArray(validators) && validators.length === 0) return;

  return validators.reduce<Validator | undefined>((result, validator) => {
    const { priority, validate } = validator;
    const isValid = validate(value);
    const hasHigherPriority = priority > (result?.priority || 0);

    if (!isValid && hasHigherPriority) return validator;
    else return result;
  }, undefined);
}

/*
Validators should be able to allow for three different scenarios:
1. Validate the input immediately (before + after any change)
2. Validate the input after an initial change
3. Validate the input on blur
4. Validate the input when an external action occurs (e.g. press the submit button)
*/

export const notNullValidator = {
  message: "This field can't be empty",
  priority: 1,
  validate(value: string): boolean {
    return !!value && value.trim() !== '';
  },
};

export const emailValidator = {
  message: "This email address isn't valid",
  priority: 1,
  validate(value: string): boolean {
    return isEmail(value?.trim() || '');
  },
};

export const urlValidator = {
  message: 'This URL is not valid',
  priority: 2,
  validate(value: string): boolean {
    return isUrl(value.trim());
  },
};

export const websiteValidator = {
  message: 'This is not a valid website URL',
  priority: 2,
  validate(value: string): boolean {
    return isURLFieldValueValid('org', value.trim());
  },
};

export const passwordValidators = [
  {
    message: 'Password must contain 1 digit or symbol',
    priority: 1,
    validate(value: string): boolean {
      return /\d|[^a-zA-Z0-9]/.test(value);
    },
  },
  {
    message: 'Password must be 10 or more characters',
    priority: 2,
    validate(value: string): boolean {
      return value.length >= 10;
    },
  },
  {
    message: 'Password must contain an uppercase letter',
    priority: 1,
    validate(value: string): boolean {
      return /[A-Z]/.test(value);
    },
  },
  {
    message: 'Password must contain a lowercase letter',
    priority: 1,
    validate(value: string): boolean {
      return /[a-z]/.test(value);
    },
  },
];

export const buildDomainValidator = (domains: string[]): Validator => {
  return {
    message: 'Email address is outside of workspace domain and cannot be auto-synced',
    priority: 0.5,
    validate(value: string): boolean {
      const domain = value.split('@')[1];
      return domains.includes(domain);
    },
    preventSubmit: false,
  };
};
